import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { coverScale } from '../artwork-texture.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { getThunderhead } from '../thunderhead-assets.js';

// Contrat du rig partagé : interdit. L'orage est une photo.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'thunderhead',
  family: 'photographic',
  danceability: 0.38,
  requiresTier: 0,
  energyLevel: 0.25,
  bpmMin: 50,
  bpmMax: 140,
  movement: 0.25,
  tonality: 0.70,
  enabled: true
};

// Thunderhead (le brief disait « Storm » ; cet id est pris par la couche
// particle-layers/storm-layer.js) : un orage lointain, la nuit, au-dessus
// d'une plaine - À PARTIR DE PHOTOS. Post-rock, ambient, musique de film,
// trip-hop, néo-classique. Preset lounge (R9) : rien ne bat sur les temps ;
// les éclairs partent sur les ACCENTS.
//
// LES PHOTOS (tools/build-thunderhead.mjs, R8) : la photo sombre, et trois
// COUCHES DE LUMIÈRE L_i = max(0, éclairée_i - sombre) - un éclair à gauche,
// au centre, à droite - rangées dans les canaux R, G, B d'une seule image
// (leur luminance ; la teinte de chaque lampe vient du manifeste). Rendu :
//   couleur = sombre + Σ w_i x L_i x teinte_i x atténuation(région)
// calculée dans le DOMAINE sRGB de la photo (les couches y ont été mesurées ;
// la photo sombre est chargée brute), convertie en linéaire à la fin pour
// l'OutputPass. Additionner des différences sRGB à une photo décodée en
// linéaire donnait un orage de plein jour.
// Les poids w (0 à 1,3) SONT les éclairs ; en combinant deux couches voisines
// on obtient un éclair à toute position intermédiaire. La lumière des couches
// hors du nuage (ciel, sol) est atténuée : l'éclairage reste dans l'orage et
// sur la plaine, l'écran ne flashe jamais en entier (R10). Masques (PNG sans
// perte, adoucis) : R ciel clair (les étoiles), G nuage (rideau compris), B
// silhouettes du premier plan (arbre, poteaux, fils) ; le sol est le reste
// sous l'horizon.
//
// UN ÉCLAIR = 2 à 4 PICS de 40 à 120 ms, séparés de 30 à 200 ms (le
// scintillement) ; chaque pic a une POSITION continue sur l'axe des couches
// (0 gauche, 1 centre, 2 droite) et les pics successifs GLISSENT d'une couche
// à la voisine : la lumière court dans l'orage. Poids d'une couche = tente
// (1 - |position - i|) x amplitude x enveloppe. Teinte par éclair : blanc,
// violet pâle ou rose.
//
// L'ÉCLAIR AU SOL : un trait ramifié par déplacement de point médian (32
// segments + 2 branches, calculé au déclenchement sur le CPU) rendu par
// distance en pixels (R1 : cœur blanc 0-2 px, gaine violet pâle 2-6 px, halo
// exp(-d/10) x 0,35), 2 ou 3 arcs en retour en 350 ms, de la BASE du nuage
// sous le centre de la couche active à un IMPACT sur l'horizon sous le
// rideau ; un pic fort (1,3) de la couche la plus proche l'accompagne ; il
// passe DERRIÈRE les silhouettes (x (1 - masque B)) et allume la plaine
// autour de l'impact.
//
// LA VIE ENTRE LES ÉCLAIRS : les nuages dérivent vers la droite (le sens de
// l'enclume) de 0,3 % de la largeur par période de 8 s - flow-map à deux
// phases comme Cloudsea, appliquée identiquement à la photo sombre et aux
// couches (le champ est nul hors du nuage : aucune couture) ; le rideau de
// pluie : fines stries verticales qui défilent dans sa boîte ; les étoiles
// scintillent dans le ciel clair ; les fils se balancent (décalage vertical
// d'une demi-pixel dans leur boîte).
//
// LE CYCLE (R20, lib/scene-clock.js, 20 s) : premier éclair avant 2 s ; un
// accent fort (adapt.attack) déclenche un éclair avec la probabilité
// 0,25 + 0,6 intensity, jamais deux départs à moins de 2 s ; une horloge
// lente en place un « de fond » après 4,2 s sans éclair ; l'éclair au sol
// vers 10 s (les 2 s avant sont gardées libres), un second parfois vers 16 s.
//
// CADRAGE (R18) : couvrant (coverScale), marge de l'écoulement comprise,
// recadrage ancré sur l'orage (centre des lampes) et, en bande large, entre
// les lampes et l'horizon.
//
// REPLI : une seule photo éclairée = une seule lampe, les éclairs varient
// seulement d'intensité ; sans photo, indisponible (disableVisualization).
//
// Debug : ?stormbolt=left|center|right|ground (déclenche), ?stormlayers=1
// (les trois couches en R, G, B), ?stormt=<s> (temps de scène),
// ?stormhold=<0..2> (une lampe tenue allumée à cette position, pour les
// vignettes).

const MAX_SEG = 48;
const SAFE_FLOOR = 0.5;
const MIN_GAP = 2.0;               // seconds between two lightning starts
const BG_GAP = 4.2;                // the slow background clock
const GROUND_AT = 10;              // scene seconds
const CYCLE = 20;
const FLOW_PERIOD = 8;             // seconds
const DISP = 0.003;                // of the width per period
const EXPOSURE = 1.0;
const TIER = { 0: { phases: 1 }, 1: { phases: 2 }, 2: { phases: 2 } };
const TINTS = [[1.0, 1.0, 1.0], [0.86, 0.80, 1.0], [1.0, 0.82, 0.92]];   // white, pale violet, pink

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const fragment = (phases) => `
  precision highp float;
  uniform sampler2D uDark, uLights, uMask, uFlow;
  uniform vec2 uFit, uCenter, uScreenPx, uImgPx;
  uniform float uAspect, uTime, uPhase, uHorizon, uDebug;
  uniform vec3 uW;                   // the three lamps' weights
  uniform mat3 uTint;                // columns: the lamps' colours (chroma x the flash's tint)
  uniform vec4 uRain;                // x0, y0, x1, y1 (image uv, y up)
  uniform vec4 uWires;
  uniform float uSway, uFlashTot;
  uniform vec4 uSeg[${MAX_SEG}];     // the ground bolt's segments (image uv)
  uniform float uSegW[${MAX_SEG}];
  uniform vec4 uBoltBox;             // its bounding box + the halo's margin (image uv)
  uniform float uBolt;
  uniform vec2 uImpact;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float hash1(float x) { return fract(sin(x * 127.1) * 43758.5453); }
  float lum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
  float inBox(vec2 p, vec4 b, float feather) {
    vec2 d = min(p - b.xy, b.zw - p);
    return smoothstep(0.0, feather, min(d.x, d.y));
  }
  void main() {
    // R18: the covering crop, flow margin included
    vec2 p = uCenter + (vUv - 0.5) * uFit;
    // the wires sway: half a pixel, in their box only
    float wb = inBox(p, uWires, 0.03);
    p.y += uSway * wb;
    vec3 m = texture2D(uMask, p).rgb;              // r sky, g cloud, b foreground
    float below = step(p.y, uHorizon);
    float ground = clamp(1.0 - m.r - m.g - m.b, 0.0, 1.0) * below;
    // the clouds flow (two phases, triangular weights); the field is 0 outside the clouds
    vec2 f = (texture2D(uFlow, p).rg - 0.5) * 2.0;
    f.y = -f.y;
    vec3 dark, lights;
${phases > 1 ? `
    float fa = fract(uPhase), fb = fract(uPhase + 0.5);
    float wa = 1.0 - abs(1.0 - 2.0 * fa);
    vec2 disp = vec2(${DISP.toFixed(4)}, ${DISP.toFixed(4)} * uImgPx.x / uImgPx.y);
    vec2 ua = p - f * disp * fa, ub = p - f * disp * fb;
    dark = texture2D(uDark, ua).rgb * wa + texture2D(uDark, ub).rgb * (1.0 - wa);
    lights = texture2D(uLights, ua).rgb * wa + texture2D(uLights, ub).rgb * (1.0 - wa);
` : `
    vec2 ua = p - f * vec2(${DISP.toFixed(4)}, 0.0) * fract(uPhase);
    dark = texture2D(uDark, ua).rgb;
    lights = texture2D(uLights, ua).rgb;
`}
    if (uDebug > 0.5) { gl_FragColor = vec4(dark * 0.25 + lights, 1.0); return; }
    // THE FLASHES: the lamps, attenuated outside the storm (R10: never the whole frame)
    float att = m.g + 0.55 * ground + 0.12 * m.r;
    vec3 light = uTint * (uW * lights) * att;
    vec3 col = dark + light;
    // THE STARS: 1 px, twinkling, in the clear sky only
    vec2 cell = floor(gl_FragCoord.xy / 3.0);
    float h = hash2(cell);
    if (h > 0.985 && m.r > 0.5) {
      vec2 sp = (cell + 0.5 + (vec2(hash2(cell + 7.1), hash2(cell + 3.3)) - 0.5) * 2.0) * 3.0;
      float d = length(gl_FragCoord.xy - sp);
      float tw = 0.6 + 0.4 * sin(uTime * (1.5 + 3.0 * fract(h * 13.0)) + h * 60.0);
      col += vec3(0.8, 0.82, 0.95) * pow(fract(h * 71.0), 2.0) * max(0.0, 1.0 - d) * tw * m.r * 0.7;
    }
    // THE RAIN CURTAIN: fine vertical streaks scrolling down in its box, lit by the flashes
    float rb = inBox(p, uRain, 0.06);
    if (rb > 0.001) {
      // one streak per image column at most, long and faint, falling
      float xpx = floor(p.x * uImgPx.x);
      float streak = pow(hash1(xpx), 9.0);
      float fall = fract(p.y * 4.0 + uTime * 0.3 + hash1(xpx + 0.5) * 3.0);
      float s = streak * smoothstep(0.35, 1.0, fall) * rb * (1.0 - m.b);
      col += vec3(0.62, 0.58, 0.78) * s * (0.02 + 0.12 * uFlashTot * m.g);
    }
    // THE GROUND BOLT (R1): white core 0-2 px, pale violet sheath 2-6 px, halo exp(-d/10) x 0.35; BEHIND the silhouettes
    if (uBolt > 0.002 && p.x > uBoltBox.x && p.x < uBoltBox.z && p.y > uBoltBox.y && p.y < uBoltBox.w) {
      vec2 toPx = uScreenPx / uFit;                  // image uv -> screen px
      float best = 1e9, wb2 = 0.0;
      for (int i = 0; i < ${MAX_SEG}; i++) {
        float wgt = uSegW[i];
        if (wgt <= 0.0) continue;
        vec4 sg = uSeg[i];
        vec2 a = sg.xy, ab = sg.zw - sg.xy;
        float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 1e-10), 0.0, 1.0);
        float d = length((p - a - ab * t) * toPx) / wgt;
        if (d < best) { best = d; wb2 = wgt; }
      }
      float core = (1.0 - smoothstep(1.0, 2.0, best)) * wb2;
      float sheath = (1.0 - smoothstep(2.0, 6.0, best)) * 0.7;
      float halo = exp(-best / 10.0) * 0.35;
      vec3 bolt = vec3(1.0, 0.98, 1.0) * core * 1.3 + vec3(0.72, 0.62, 0.98) * sheath * 0.6 + vec3(0.6, 0.5, 0.92) * halo;
      col += uBolt * bolt * (1.0 - m.b);
    }
    // the plain lit around the impact
    col += vec3(0.42, 0.36, 0.55) * uBolt * ground * 0.35 * exp(-abs(p.x - uImpact.x) / 0.18) * exp(-(uHorizon - p.y) / 0.08);
    col *= ${EXPOSURE.toFixed(2)};
    // comfort: a soft knee from 0.6 to under the bloom threshold (0.78)
    float L = lum(col);
    float k = L > 0.6 ? (0.6 + 0.14 * (1.0 - exp(-(L - 0.6) / 0.14))) / L : 1.0;
    col *= k;
    // the cover's mask (R19: it follows the cover)
    vec2 s2 = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s2 - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    col += (hash2(gl_FragCoord.xy + fract(uTime)) - 0.5) / 255.0;
    // everything above was composited in the sRGB domain (the photo's, where
    // the light layers were measured); OutputPass re-encodes, so hand it linear
    gl_FragColor = vec4(pow(max(col, 0.0), vec3(2.2)), 1.0);
  }
`;

export class ThunderheadVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const t0 = parseFloat(urlParam('stormt'));
    this._clock = new SceneClock({ period: CYCLE, start: Number.isFinite(t0) && t0 >= 0 ? (t0 % CYCLE) / CYCLE : 0 });
    this._boltParam = urlParam('stormbolt');
    this._debug = urlParam('stormlayers') === '1' ? 1 : 0;
    const hold = parseFloat(urlParam('stormhold'));            // captures: a lamp held lit at this position (0 left .. 2 right)
    this._hold = Number.isFinite(hold) ? hold : null;
    this._adapt = new Adapt();
    this._post = { bloomStrength: 0.35, bloomThreshold: 0.78, bloomRadius: 0.5, vignette: 0.25, grain: 0.0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    const A = getThunderhead();
    this._assets = A;
    if (!A) return;                                   // withdrawn by the entry point; nothing to draw
    const man = A.manifest;
    this._man = man;
    // the manifest's y are top-down (image rows): flipped once here
    const Y = (y) => 1 - y;
    this._layers = man.layers.map((l) => ({ name: l.name, cx: l.centre[0], cy: Y(l.centre[1]), base: Y(l.base), chroma: l.chroma, impact: [l.impact[0], Y(l.impact[1])] }));
    this._horizon = Y(man.horizon);
    this._impacts = (man.impacts || []).map((p) => [p[0], Y(p[1])]);
    const box = (b) => b ? new THREE.Vector4(b.x0, Y(b.y1), b.x1, Y(b.y0)) : new THREE.Vector4(9, 9, -9, -9);
    const segs = [], segW = [];
    for (let i = 0; i < MAX_SEG; i++) { segs.push(new THREE.Vector4()); segW.push(0); }
    this._u = {
      uDark: { value: A.dark }, uLights: { value: A.lights }, uMask: { value: A.masks }, uFlow: { value: A.flow },
      uFit: { value: new THREE.Vector2(1, 1) }, uCenter: { value: new THREE.Vector2(0.5, 0.5) }, uScreenPx: { value: new THREE.Vector2(1280, 720) },
      uImgPx: { value: new THREE.Vector2(man.size[0], man.size[1]) },
      uAspect: safeZoneUniforms.uAspect, uTime: { value: 0 }, uPhase: { value: 0 }, uHorizon: { value: this._horizon }, uDebug: { value: this._debug },
      uW: { value: new THREE.Vector3() }, uTint: { value: new THREE.Matrix3() },
      uRain: { value: box(man.rain) }, uWires: { value: box(man.wires) }, uSway: { value: 0 }, uFlashTot: { value: 0 },
      uSeg: { value: segs }, uSegW: { value: segW }, uBoltBox: { value: new THREE.Vector4(9, 9, -9, -9) }, uBolt: { value: 0 }, uImpact: { value: new THREE.Vector2() },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._mat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: quadVertex, fragmentShader: fragment(this._layout.phases), depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._seed = 7771;
    this._now = 0; this._phase = 0;
    this._peaks = [];                 // { t0, dur, amp, pos, tint }
    this._bolt = null;                // { t0, dur, peaks, impactX }
    this._lastStart = -99; this._count = 0; this._groundCount = 0; this._groundDone = -1; this._ground2Done = -1;
    this._log = [];
    this._firstAt = this._clock.elapsed < 1.5 ? 0.6 + 0.8 * this._rnd() : 0;
    this._forced = null;
    if (this._boltParam && ['left', 'center', 'right', 'ground'].indexOf(this._boltParam) >= 0) { this._forced = this._boltParam; this._firstAt = 0.15; }
    this._setTint(0);
  }

  _rnd() { this._seed = (this._seed * 16807) % 2147483647; return this._seed / 2147483647; }
  _setTint(k) {
    const t = TINTS[k], L = this._layers, M = this._u.uTint.value.elements;
    for (let i = 0; i < 3; i++) {
      const l = L[Math.min(i, L.length - 1)];
      M[i * 3] = l.chroma[0] * t[0]; M[i * 3 + 1] = l.chroma[1] * t[1]; M[i * 3 + 2] = l.chroma[2] * t[2];
    }
  }
  // a flash: 2-4 peaks of 40-120 ms, 30-200 ms apart, sliding from one lamp to the next
  _fireChain(t, strength, startName) {
    const n = this._layers.length;
    const np = 2 + Math.floor(this._rnd() * 3);
    let pos = startName ? this._layers.findIndex((l) => l.name === startName) : Math.floor(this._rnd() * n);
    if (pos < 0) pos = 0;
    let dir = pos === 0 ? 1 : pos === n - 1 ? -1 : (this._rnd() < 0.5 ? -1 : 1);
    let start = t;
    for (let k = 0; k < np; k++) {
      const dur = 0.04 + 0.08 * this._rnd();
      this._peaks.push({ t0: start, dur, amp: strength * (k === 0 ? 1 : 0.6 + 0.5 * this._rnd()), pos });
      start += dur + 0.03 + 0.17 * this._rnd();
      // slide toward the neighbour: 0.5 to 1 lamp per peak, bouncing at the ends
      let step = (0.5 + 0.5 * this._rnd()) * dir;
      if (pos + step > n - 1 || pos + step < 0) { dir = -dir; step = -step; }
      pos = Math.max(0, Math.min(n - 1, pos + step));
    }
    this._setTint(Math.floor(this._rnd() * TINTS.length));
    this._lastStart = t; this._count++; this._firstAt = -1;
    this._log.push({ t: +t.toFixed(2), kind: 'chain', n: np });
  }
  _fireGround(t) {
    const L = this._layers;
    const li = Math.floor(this._rnd() * L.length), l = L[li];
    // the impact: the candidate nearest the lamp (a little jitter picks a neighbour now and then)
    let imp = l.impact, best = 1e9;
    for (const p of this._impacts.concat([l.impact])) { const d = Math.abs(p[0] - l.cx) + 0.12 * this._rnd(); if (d < best) { best = d; imp = p; } }
    const top = [l.cx + (this._rnd() - 0.5) * 0.06, l.base];
    const end = [imp[0], this._horizon + 0.003];
    let pts = [top, end];
    let amp = 0.05;
    for (let lvl = 0; lvl < 5; lvl++) {
      const next = [pts[0]];
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i], b = pts[i + 1];
        next.push([(a[0] + b[0]) / 2 + (this._rnd() - 0.5) * amp, (a[1] + b[1]) / 2 + (this._rnd() - 0.5) * amp * 0.3], b);
      }
      pts = next; amp *= 0.55;
    }
    const segs = this._u.uSeg.value, w = this._u.uSegW.value;
    let n = 0;
    const push = (a, b, wgt) => { if (n < MAX_SEG) { segs[n].set(a[0], a[1], b[0], b[1]); w[n] = wgt; n++; } };
    for (let i = 0; i + 1 < pts.length; i++) push(pts[i], pts[i + 1], 1);
    for (let b = 0; b < 2; b++) {
      const i0 = 3 + Math.floor(this._rnd() * 12);
      let p = pts[i0], dir = (this._rnd() < 0.5 ? -1 : 1);
      for (let k = 0; k < 8; k++) {
        const q = [p[0] + dir * (0.004 + 0.009 * this._rnd()), p[1] - (0.003 + 0.008 * this._rnd())];
        push(p, q, 0.5); p = q;
      }
    }
    for (let i = n; i < MAX_SEG; i++) w[i] = 0;
    let x0 = 9, y0 = 9, x1 = -9, y1 = -9;
    for (let i = 0; i < n; i++) { const s = segs[i]; x0 = Math.min(x0, s.x, s.z); x1 = Math.max(x1, s.x, s.z); y0 = Math.min(y0, s.y, s.w); y1 = Math.max(y1, s.y, s.w); }
    const mg = 0.06;
    this._u.uBoltBox.value.set(x0 - mg, y0 - mg, x1 + mg, y1 + mg);
    this._u.uImpact.value.set(end[0], end[1]);
    const peaks = [[0.02, 1, 0.02], [0.13, 0.65, 0.025]];
    if (this._rnd() < 0.6) peaks.push([0.27, 0.8, 0.025]);
    this._bolt = { t0: t, dur: 0.45, peaks };
    // the nearest lamp fires hard with it
    for (const [pt, a, wd] of peaks) this._peaks.push({ t0: t + pt - wd, dur: 2.4 * wd, amp: 1.3 * a, pos: li });
    this._setTint(0);
    this._lastStart = t; this._count++; this._groundCount++;
    this._log.push({ t: +t.toFixed(2), kind: 'ground', lamp: l.name });
  }
  _env(peaks, tt, dur) {
    if (tt < 0 || tt > dur + 0.15) return 0;
    let e = 0;
    for (const [pt, a, w] of peaks) { const q = (tt - pt) / w; e += a * Math.exp(-q * q); }
    return e;
  }

  update(deltaTime, profile) {
    if (!this._assets) return;
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    const a = this._adapt.update(dt, profile);
    this._clock.update(dt);
    if (dt === 0) return;                         // paused: the storm holds its frame
    this._now += dt;
    const t = this._now, I = a.intensity;
    const sceneT = this._clock.t, sceneIdx = this._clock.cycle;
    // no pulse (R9): the intensity only sets the drift's pace a little
    this._phase = (this._phase + dt / FLOW_PERIOD * (0.8 + 0.4 * I)) % 1024;
    this._u.uPhase.value = this._phase;
    this._u.uTime.value = t % 1000;
    this._u.uSway.value = 0.0007 * Math.sin(t * 0.9) * (0.6 + 0.4 * Math.sin(t * 0.37 + 1.0));

    // THE SCRIPT
    const free = t - this._lastStart >= MIN_GAP;
    if (this._firstAt > 0 && t >= this._firstAt && free) {
      this._firstAt = -1;
      if (this._forced === 'ground') this._fireGround(t); else this._fireChain(t, 1, this._forced);
    } else if (this._groundDone !== sceneIdx && sceneT >= GROUND_AT && free) { this._groundDone = sceneIdx; this._fireGround(t); }
    else if (this._ground2Done !== sceneIdx && sceneT >= 16 && sceneT < 18.5 && free && this._rnd() < 0.012) { this._ground2Done = sceneIdx; this._fireGround(t); }
    else {
      const groundSoon = this._groundDone !== sceneIdx && sceneT >= GROUND_AT - MIN_GAP && sceneT < GROUND_AT;
      if (groundSoon) { /* hold: the ground bolt lands at 10 s */ }
      else if (a.attack > 0.55 && free && this._rnd() < 0.25 + 0.6 * I) this._fireChain(t, 0.75 + 0.35 * a.attack);
      else if (t - this._lastStart >= BG_GAP && this._firstAt <= 0) this._fireChain(t, 0.8);
    }

    // THE LAMPS NOW: tent weights of every live peak, capped at 1.3 per lamp
    const w = this._u.uW.value; w.set(0, 0, 0);
    this._peaks = this._peaks.filter((p) => t - p.t0 < p.dur + 0.05);
    let tot = 0;
    for (const p of this._peaks) {
      const q = (t - p.t0 - p.dur / 2) / (p.dur / 2.2);
      const e = p.amp * Math.exp(-q * q);
      if (e < 0.003) continue;
      for (let i = 0; i < 3; i++) { const k = Math.max(0, 1 - Math.abs(p.pos - i)); if (k > 0) { const v = Math.min(1.3, w.getComponent(i) + e * k); w.setComponent(i, v); } }
      tot += e;
    }
    this._u.uFlashTot.value = Math.min(1.5, tot);
    if (this._hold != null) { for (let i = 0; i < 3; i++) w.setComponent(i, Math.max(0, 1 - Math.abs(this._hold - i))); this._u.uFlashTot.value = 1; }
    const b = this._bolt ? this._env(this._bolt.peaks, t - this._bolt.t0, this._bolt.dur) : 0;
    if (this._bolt && t - this._bolt.t0 > this._bolt.dur + 0.2) this._bolt = null;
    this._u.uBolt.value = Math.min(1, b);
  }

  // R18: cover the screen (the flow's margin included), anchored on the storm
  _frame(aspect) {
    const sz = this._u.uImgPx.value;
    const fit = coverScale(aspect, sz.x / sz.y).multiplyScalar(1 / (1 + 2 * DISP));
    this._u.uFit.value.copy(fit);
    const L = this._layers;
    const sx = L.reduce((s, l) => s + l.cx, 0) / L.length, sy = L.reduce((s, l) => s + l.cy, 0) / L.length;
    const cx = fit.x < 1 ? Math.min(1 - fit.x / 2, Math.max(fit.x / 2, sx)) : 0.5;
    // a wide band keeps both the lamps and the horizon
    const want = 0.5 * (sy + this._horizon);
    const cy = fit.y < 1 ? Math.min(1 - fit.y / 2, Math.max(fit.y / 2, want)) : 0.5;
    this._u.uCenter.value.set(cx, cy);
  }

  _render(renderer) {
    if (!this._assets) return;
    renderer.getDrawingBufferSize(this._size);
    const W = Math.max(2, this._size.x), H = Math.max(2, this._size.y);
    const aspect = W / H;
    if (aspect !== this._aspect) { this._aspect = aspect; this._frame(aspect); }
    this._u.uScreenPx.value.set(W, H);
  }

  get probe() {
    const w = this._u ? this._u.uW.value : null;
    return { t: +this._now.toFixed(2), scene: +this._clock.t.toFixed(2), count: this._count, ground: this._groundCount,
      w: w ? [+w.x.toFixed(2), +w.y.toFixed(2), +w.z.toFixed(2)] : null, flash: this._u ? +this._u.uFlashTot.value.toFixed(2) : 0,
      bolt: this._u ? +this._u.uBolt.value.toFixed(2) : 0, layers: this._layers ? this._layers.length : 0, log: this._log, intensity: +this._adapt.get().intensity.toFixed(2) };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); this._mat.dispose(); }
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}
